import React, { useMemo, useState } from "react";
import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import { usePlatform } from "../platform/store";
import {
  DataTable,
  InlineActions,
  PageRoot,
  PrimaryAction,
  RecordCard,
  SectionCard,
  StatusChip,
  SummaryGrid,
} from "./ui/PlatformPrimitives";

const VendorManagement = () => {
  const { vendors, addVendor, addVendorCertification } = usePlatform();
  const [selectedVendorId, setSelectedVendorId] = useState(vendors[0]?.id || "");
  const [newVendor, setNewVendor] = useState({ name: "", region: "EU", tier: "Core", contact: "" });
  const [newCertification, setNewCertification] = useState({
    type: "IFRA Certificate",
    status: "Active",
    expirationDate: "2026-12-31",
  });
  const [errorMessage, setErrorMessage] = useState("");

  const selectedVendor = vendors.find((vendor) => vendor.id === selectedVendorId) || vendors[0];
  const atRisk = useMemo(
    () =>
      vendors.filter(
        (vendor) =>
          vendor.missingDocuments.length > 0 ||
          vendor.certifications.some((certification) => certification.status !== "Active")
      ),
    [vendors]
  );

  return (
    <PageRoot>
      {errorMessage ? <Typography color="error">{errorMessage}</Typography> : null}
      <SummaryGrid
        items={[
          ["Vendors", vendors.length],
          ["At risk", atRisk.length],
          ["Missing docs", vendors.reduce((count, vendor) => count + vendor.missingDocuments.length, 0)],
          ["Strategic", vendors.filter((vendor) => vendor.tier === "Strategic").length],
        ]}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <SectionCard eyebrow="Directory" title="Vendor network">
            <Grid container spacing={2}>
              {vendors.map((vendor) => (
                <Grid key={vendor.id} size={{ xs: 12 }}>
                  <RecordCard
                    title={vendor.name}
                    subtitle={vendor.missingDocuments.length ? vendor.missingDocuments.join(", ") : "No missing documents"}
                    status={vendor.complianceStatus}
                    chips={[vendor.region, vendor.tier]}
                    selected={selectedVendor?.id === vendor.id}
                    onClick={() => setSelectedVendorId(vendor.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <SectionCard eyebrow="Selected" title={selectedVendor?.name || "Vendor"}>
            {selectedVendor ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard title="Status">
                    <Typography variant="body1" color="text.secondary">Contact: {selectedVendor.contact}</Typography>
                    <Typography variant="body1" color="text.secondary">Documents: {selectedVendor.documents.join(", ") || "None"}</Typography>
                    <Typography variant="body1" color="text.secondary">
                      Missing: {selectedVendor.missingDocuments.length ? selectedVendor.missingDocuments.join(", ") : "None"}
                    </Typography>
                  </SectionCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard title="Add certification">
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Type"
                          value={newCertification.type}
                          onChange={(event) => setNewCertification((current) => ({ ...current, type: event.target.value }))}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          select
                          label="Status"
                          value={newCertification.status}
                          onChange={(event) => setNewCertification((current) => ({ ...current, status: event.target.value }))}
                        >
                          {["Active", "Pending Renewal", "Expired", "Missing"].map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Expiration"
                          value={newCertification.expirationDate}
                          onChange={(event) =>
                            setNewCertification((current) => ({ ...current, expirationDate: event.target.value }))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <PrimaryAction
                          onClick={async () => {
                            try {
                              setErrorMessage("");
                              await addVendorCertification({ vendorId: selectedVendor.id, certification: newCertification });
                            } catch (error) {
                              setErrorMessage(error.message || "Unable to save certification.");
                            }
                          }}
                        >
                          Save certification
                        </PrimaryAction>
                      </Grid>
                    </Grid>
                  </SectionCard>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <SectionCard title="Certifications">
                    <DataTable
                      columns={[
                        { key: "type", label: "Type" },
                        {
                          key: "status",
                          label: "Status",
                          render: (row) => <StatusChip status={row.status} />,
                        },
                        { key: "expirationDate", label: "Expiration" },
                      ]}
                      rows={selectedVendor.certifications}
                      getRowKey={(row) => `${row.type}-${row.expirationDate}`}
                    />
                  </SectionCard>
                </Grid>
              </Grid>
            ) : null}
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard eyebrow="Create" title="New vendor">
        <InlineActions>
          <TextField
            size="small"
            label="Name"
            value={newVendor.name}
            onChange={(event) => setNewVendor((current) => ({ ...current, name: event.target.value }))}
          />
          <TextField
            size="small"
            label="Contact"
            value={newVendor.contact}
            onChange={(event) => setNewVendor((current) => ({ ...current, contact: event.target.value }))}
          />
          <TextField
            size="small"
            select
            label="Region"
            value={newVendor.region}
            onChange={(event) => setNewVendor((current) => ({ ...current, region: event.target.value }))}
          >
            <MenuItem value="EU">EU</MenuItem>
            <MenuItem value="US">US</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Tier"
            value={newVendor.tier}
            onChange={(event) => setNewVendor((current) => ({ ...current, tier: event.target.value }))}
          >
            <MenuItem value="Strategic">Strategic</MenuItem>
            <MenuItem value="Core">Core</MenuItem>
          </TextField>
          <PrimaryAction
            onClick={async () => {
              try {
                setErrorMessage("");
                await addVendor(newVendor);
                setNewVendor({ name: "", region: "EU", tier: "Core", contact: "" });
              } catch (error) {
                setErrorMessage(error.message || "Unable to add vendor.");
              }
            }}
            disabled={!newVendor.name.trim()}
          >
            Add vendor
          </PrimaryAction>
        </InlineActions>
      </SectionCard>
    </PageRoot>
  );
};

export default VendorManagement;
